import type { ApolloError } from '@apollo/client'
import {
  EmployeesLegalEntityRolesDocument,
  EmployeesLegalEntityRolesQuery,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

interface SalaryRange {
  id: string
  minRange: number
  maxRange: number
  currency: string
  amountType: string
  salaryFrequency: string
}
export interface Level {
  id: string
  name: string
  isDefault: boolean
  salaryRanges: SalaryRange[]
}

export interface Role {
  id: string
  name: string
  levels: Level[]
}

type RolesCollection = Record<string, Role>

const mapData = (
  data: EmployeesLegalEntityRolesQuery | undefined,
  legalEntityId: string
): RolesCollection => {
  if (!data) return {}

  const roles: Role[] = data.jobCatalog.roles.map((role) => ({
    id: role.id,
    name: role.name,
    levels: role.levels.map((level) => ({
      id: level.id,
      name: level.name,
      isDefault: level.isDefault,
      salaryRanges: level.salaryRanges
        .filter((salaryRange) =>
          salaryRange.legalEntities.some((legalEntity) => legalEntity.id === legalEntityId)
        )
        .map((salaryRange) => ({
          id: salaryRange.id,
          minRange: salaryRange.minRange,
          maxRange: salaryRange.maxRange,
          currency: salaryRange.currency,
          amountType: salaryRange.amountType,
          salaryFrequency: salaryRange.salaryFrequency,
        })),
    })),
  }))

  return roles.reduce((acc: Record<string, Role>, role: Role) => {
    acc[role.id] = role
    return acc
  }, {})
}

type Return = [RolesCollection | undefined, boolean, () => void, ApolloError | undefined]

export const useLegalEntityRoles = (legalEntityId: string): Return => {
  const { data, loading, refetch, error } = useQuery(EmployeesLegalEntityRolesDocument, {
    variables: { legalEntityId },
  })

  const roles = mapData(data, legalEntityId)

  return [roles, loading, refetch, error]
}
